import { type FC, useEffect } from 'react'

import { Expand } from '@liveHelper/assets/Expand'
import { Minimize } from '@liveHelper/assets/Minimize'
import { SETTINGS_MODAL } from '@liveHelper/components/OneToOneModal/modalIds'
import { RecordingStatement } from '@liveHelper/components/RecordingStatement'
import { OnCallVideoContainer } from '@liveHelper/components/shared/OnCallVideoContainer'
import { VideoButtonRow } from '@liveHelper/components/shared/VideoButtonRow'
import { useVisibilityToggle } from '@liveHelper/hooks/useVisibilityToggle'
import { selectors } from '@liveHelper/models/modal/modalRegistry'
import { actionsSelector, useStore } from '@liveHelper/models/store'
import { useChatTheme } from '@oneToOne/shared/components/chat/theme/useChatTheme'
import { ChatHeader } from '@oneToOne/shared/components/twind/ChatHeader'
import { ActionButton } from '@oneToOne/shared/styles'
import type { IAgoraRTCRemoteUser, ILocalVideoTrack } from 'agora-rtc-sdk-ng'

import { useContextTranslation } from '@core/i18n'
import { isMobileWeb } from '@core/userAgent'

import {
  ActionContainer,
  ButtonRowWrapper,
  ChatHeaderOnCallWrapper,
  Column,
} from './ChatHeaderOnCall.styles'

interface Props {
  agentRTCUser: IAgoraRTCRemoteUser | undefined
  localVideoTrack: ILocalVideoTrack | undefined
}

/**
 *  Chatting Header Component for when a call is active
 */
export const ChatHeaderOnCall: FC<Props> = ({
  agentRTCUser,
  localVideoTrack,
}) => {
  const { t } = useContextTranslation()
  const { theme } = useChatTheme()
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const { isVisible, ref } = useVisibilityToggle()
  const { hideModal } = useStore(selectors.modalActionsSelector)

  useEffect(() => {
    return () => {
      hideModal(SETTINGS_MODAL)
    }
  }, [hideModal])

  return (
    <Column>
      <ChatHeaderOnCallWrapper theme={theme} ref={ref}>
        <ActionContainer>
          <ActionButton
            aria-label={t(`oneToOne:Minimize Live Helper Dialog`)}
            actionType="neutral"
            buttonSize="sm"
            shape="square"
            onClick={() => {
              sendUIStateTransitionEvent('MINIMIZE')
            }}
            icon={<Minimize width="16px" fill="white" label="minimize" />}
          />
          {!isMobileWeb() ? (
            <ActionButton
              aria-label={t(`oneToOne:Expand Live Helper Dialog`)}
              actionType="neutral"
              buttonSize="sm"
              shape="square"
              onClick={() => {
                sendUIStateTransitionEvent('EXPAND')
              }}
              icon={<Expand width="18px" fill="white" label="expand" />}
            />
          ) : null}
        </ActionContainer>
        <OnCallVideoContainer
          displayMode="minimized"
          agentUser={agentRTCUser}
          localVideoTrack={localVideoTrack}
        />
        <ButtonRowWrapper>
          <VideoButtonRow isVisible={isVisible} isOnCall={true} />
        </ButtonRowWrapper>
      </ChatHeaderOnCallWrapper>
      <ChatHeader title={t(`oneToOne:Chat`)} />
      <RecordingStatement isOverlayOnChat={true} />
    </Column>
  )
}
